import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useCombobox } from 'downshift';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
	getCurrentSiteUrl,
	getSearchApiUrl,
	submitTermSearch,
} from '../helpers';

import Overlay from '../components/Overlay/Overlay';
import ResultsContainer from '../components/ResultsContainer/ResultsContainer';
import SearchIcon from '../components/SearchIcon/SearchIcon';
import SearchInput from '../components/SearchInput/SearchInput';
import classNames from 'classnames/bind';
import { isViUpdate } from '../helpers';
import styles from './SearchContainer.scss';
import tagging from '../helpers/tagging';
import { useStateValue } from '../state';

const cx = classNames.bind(styles);
let abortController;
let abortSignal;

const SearchContainer = () => {
	const [contextState, dispatch] = useStateValue();
	const [isMobileOpen, setMobileOpen] = useState(false);
	const [originalInputValue, setOriginalInputValue] = useState('');
	const [searchResults, setSearchResults] = useState([]);
	const [pauseFetch, setPauseFetch] = useState(false);

	const inputRef = useRef(null);

	const { useLiberatedPredictiveSearch } = useFlags();

	const {
		isOpen,
		getComboboxProps,
		getInputProps,
		getItemProps,
		getLabelProps,
		getMenuProps,
		highlightedIndex,
		inputValue,
		openMenu,
		closeMenu,
		reset,
		selectedItem,
		setInputValue,
	} = useCombobox({
		id: 'siteSearch',
		inputId: 'siteSearch-input',
		items: searchResults,

		stateReducer: (state, actionAndChanges) => {
			// Debug
			// console.log('stateReducer', { state, actionAndChanges });

			const {
				InputKeyDownEscape,
				InputKeyDownArrowDown,
				InputKeyDownArrowUp,
				InputChange,
				InputBlur,
				ItemClick,
				InputKeyDownEnter,
				FunctionReset,
			} = useCombobox.stateChangeTypes;

			switch (actionAndChanges.type) {
				case InputKeyDownEscape: {
					if (abortController && abortController.abort) {
						abortController.abort();
					}

					setSearchResults([]);

					return {
						...actionAndChanges.changes,
						isOpen: contextState.isPopularSearchesEnabled || false,
					};
				}

				case InputKeyDownArrowDown:
				case InputKeyDownArrowUp: {
					setPauseFetch(true);

					const _highlightedIndex = actionAndChanges.changes.highlightedIndex;

					const isPopularSearches = searchResults.every(
						(x) => x.PredictiveSearchType === 'PopularSearches'
					);

					if (
						searchResults.length > 0 &&
						(inputValue.length > 0 || isPopularSearches)
					) {
						const highlightedResult = searchResults[_highlightedIndex];

						if (state.highlightedIndex === -1 && state.inputValue) {
							setOriginalInputValue(state.inputValue);
						}

						const wasAtTheTop =
							InputKeyDownArrowUp === actionAndChanges.type &&
							state.highlightedIndex === 0 &&
							_highlightedIndex === searchResults.length - 1;

						const wasAtTheBottom =
							InputKeyDownArrowDown === actionAndChanges.type &&
							state.highlightedIndex === searchResults.length - 1 &&
							_highlightedIndex === 0;

						if (wasAtTheTop || wasAtTheBottom) {
							return {
								...actionAndChanges.changes,
								inputValue: originalInputValue,
								highlightedIndex: -1,
							};
						}

						if (highlightedResult) {
							return {
								...actionAndChanges.changes,
								inputValue: highlightedResult.Title,
							};
						}
					}

					return { ...actionAndChanges.changes, isOpen: false };
				}

				case InputChange: {
					const { inputValue } = actionAndChanges.changes;

					if (inputValue.length === 0) {
						if (
							abortController &&
							typeof abortController.abort === 'function'
						) {
							abortController.abort();
						}
						setPauseFetch(true);
						setSearchResults([]);
					} else {
						setPauseFetch(false);
					}

					return {
						...actionAndChanges.changes,
						isOpen:
							contextState.isPopularSearchesEnabled || inputValue.length > 0,
					};
				}

				case InputBlur: {
					const { selectedItem } = actionAndChanges.changes;

					document.body.classList.remove('search-activated');

					if (selectedItem) {
						return {
							...actionAndChanges.changes,
							inputValue: selectedItem.Title,
						};
					}

					return actionAndChanges.changes;
				}

				case ItemClick:
				case InputKeyDownEnter: {
					const { selectedItem } = actionAndChanges.changes;

					let suggestedProducts;

					if (searchResults && searchResults.length) {
						suggestedProducts = searchResults.filter(
							(result) => result.PredictiveSearchType === 'Product'
						);
					}

					if (selectedItem) {
						tagging.initTagging({
							...contextState,
							suggestedProducts,
							selectedResultText: selectedItem.Title,
							selectedResultType: selectedItem.PredictiveSearchType,
						});

						if (
							selectedItem.PredictiveSearchType === 'Keyword' ||
							selectedItem.PredictiveSearchType === 'PopularSearches'
						) {
							submitTermSearch(contextState.clientCode, selectedItem.Title);
						}

						if (selectedItem.PredictiveSearchType === 'Product') {
							window.location.href =
								getCurrentSiteUrl(contextState.clientCode) +
								selectedItem.Destination;
						}

						return {
							...actionAndChanges.changes,
							inputValue: selectedItem.Title,
						};
					}

					if (state.inputValue && state.inputValue.length) {
						tagging.initTagging({ ...contextState, suggestedProducts });
						submitTermSearch(contextState.clientCode, state.inputValue);
					}

					return actionAndChanges.changes;
				}

				case FunctionReset: {
					if (abortSignal && abortSignal.abort) {
						abortSignal.abort();
					}

					setSearchResults([]);

					return actionAndChanges.changes;
				}

				default: {
					const { selectedItem } = actionAndChanges.changes;

					if (selectedItem) {
						return {
							...actionAndChanges.changes,
							inputValue: selectedItem.Title,
						};
					}

					return actionAndChanges.changes;
				}
			}
		},
	});

	// Dispatch StateProvider updates outside of useComboBox to avoid https://fb.me/setstate-in-render errors
	useEffect(() => {
		dispatch({
			type: 'setRawUserInput',
			rawUserInput: inputValue,
		});
	}, [inputValue]);

	useEffect(() => {
		if (!selectedItem) return;
		dispatch({
			type: 'setSelectedResult',
			selectedResultText: selectedItem.Title,
			selectedResultType: selectedItem.PredictiveSearchType,
		});
	}, [selectedItem]);

	useEffect(() => {
		let cancelled = false;

		async function fetchData() {
			if (typeof AbortController !== 'undefined') {
				abortController = new AbortController();
				abortSignal = abortController.signal;
			}

			const { clientCode } = contextState;

			try {
				const response = await fetch(
					getSearchApiUrl(inputValue, clientCode, useLiberatedPredictiveSearch),
					{
						signal: abortSignal,
					}
				);

				if (!cancelled && !pauseFetch && inputValue.length) {
					const resResults = await response.json();
					setSearchResults(resResults);
				} else {
					// Disabled to fix issues where search results dissapear
					// TODO: Optimise fetching of data
					// setSearchResults([]);
				}
			} catch {
				// Disabled to fix issues where search results dissapear
				// TODO: Optimise fetching of data
				// setSearchResults([]);
			}
		}

		if (!pauseFetch && inputValue.length) {
			fetchData();
		}

		return () => {
			cancelled = true;
		};
	}, [inputValue, setSearchResults]);

	useEffect(() => {
		if (
			inputValue.length === 0 &&
			searchResults.length === 0 &&
			contextState.isPopularSearchesEnabled
		) {
			const predictiveSearchType = 'PopularSearches';
			const popularSearches = [
				{
					PredictiveSearchType: predictiveSearchType,
					Title: 'Waschmaschinen',
				},
				{
					PredictiveSearchType: predictiveSearchType,
					Title: 'Kühlen & Gefrieren',
				},
				{
					PredictiveSearchType: predictiveSearchType,
					Title: 'Staubsauger',
				},
				{
					PredictiveSearchType: predictiveSearchType,
					Title: 'Backöfen',
				},
				{
					PredictiveSearchType: predictiveSearchType,
					Title: 'Trockner',
				},
			];
			setSearchResults(popularSearches);
		}
	}, [inputValue, searchResults]);

	let overlayRoot;
	if (typeof document !== 'undefined') {
		overlayRoot =
			document.getElementById('header__overlay-container') || document.body;
	}
	const handleMobileIconClick = useCallback(() => {
		setMobileOpen(true);
		inputRef.current && inputRef.current.focus();
	}, []);

	/** Temporary transition logic to maintain functionality while header service is being deployed */
	const [isTransition, setTransition] = useState(false);
	useEffect(() => {
		if (typeof document == 'undefined') return;

		if (document.querySelector('.search-icon__container')) {
			setTransition(true);
		}
	}, []);

	const handleMobileCloseClick = useCallback(() => {
		setMobileOpen(false);

		if (isTransition) {
			document
				.querySelector('.header-brandbar__search')
				.classList.add('hideTop');
		}
	}, [isTransition]);

	const handleOverlayClick = useCallback(() => {
		closeMenu();
		handleMobileCloseClick();
	}, [closeMenu, handleMobileCloseClick]);

	return (
		<>
			{isViUpdate() && !isTransition && (
				<SearchIcon onClick={handleMobileIconClick} />
			)}
			<div
				className={cx(
					'siteSearch',
					'bg-white',
					'absolute lg:relative',
					'left-0 lg:top-0',
					'flex',
					'items-center',
					'w-full',
					'h-16 lg:h-auto',
					'z-30'
				)}
				style={
					isViUpdate() && !isTransition
						? { top: isMobileOpen ? 0 : '-4rem' }
						: {}
				}>
				<div
					className={cx(
						'box-border',
						'relative',
						'flex',
						'flex-col',
						'font-body',
						'items-stretch',
						'pl-2 lg:pl-0',
						'w-full'
					)}
					data-testid="siteSearch">
					<div className="flex">
						<label className="visuallyhidden" {...getLabelProps()}>
							{contextState._t.label}
						</label>
						<SearchInput
							setInputValue={setInputValue}
							getInputProps={getInputProps}
							getComboboxProps={getComboboxProps}
							openCbMenu={openMenu}
							cbReset={reset}
							searchResults={searchResults}
							enableButtons={inputValue.length >= 1 ? true : false}
							isOpen={isOpen}
							ref={inputRef}
						/>
						{isViUpdate() && (
							<button
								type="button"
								className={cx(
									'bg-transparent',
									'border-0',
									'cursor-pointer',
									'lg:hidden',
									contextState.clientCode === 'ADE' ? 'w-20' : 'w-16'
								)}
								onClick={handleMobileCloseClick}>
								<i
									href="#"
									target="_self"
									className="flex ml-1 not-italic siteSearch__button--close text-body-xs">
									{contextState._t.close}
								</i>
							</button>
						)}
					</div>
					<ResultsContainer
						isOpen={isOpen}
						getMenuProps={getMenuProps}
						highlightedIndex={highlightedIndex}
						getItemProps={getItemProps}
						searchResults={searchResults}
						inputValue={contextState.rawUserInput}
						clientCode={contextState.clientCode}
					/>
				</div>
			</div>
			{overlayRoot &&
				ReactDOM.createPortal(
					<Overlay
						isOpen={isOpen || isMobileOpen}
						onClick={handleOverlayClick}
					/>,
					overlayRoot
				)}
		</>
	);
};

export default SearchContainer;
